import { useEffect } from "react";
import { motion } from "framer-motion";

const SITE_ORIGIN = "https://www.safetysymbiosis.com";
const SHARE_IMAGE_URL = `${SITE_ORIGIN}/safetysymbiosis-logo.png`;

const setMeta = (selector, attr, value, content) => {
  if (!content) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const PageMeta = ({ title, description, noindex = false }) => {
  const canonicalUrl = `${SITE_ORIGIN}${window.location.pathname}`;
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', "property", "og:title", title);
    }
    if (description) {
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", description);
      setMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        description,
      );
    }
    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.setAttribute("content", "index, follow");
    }
    setCanonical(canonicalUrl);
    setMeta(
      'meta[property="og:image"]',
      "property",
      "og:image",
      SHARE_IMAGE_URL,
    );
    setMeta(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      SHARE_IMAGE_URL,
    );
    setMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );
  }, [title, description, noindex, canonicalUrl]);
  return null;
};

export const Reveal = ({ children, delay = 0, className, y = 22 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.55, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

export const SectionHead = ({ eyebrow, title, intro, light = false }) => (
  <Reveal className="max-w-3xl">
    {eyebrow && (
      <span className={`eyebrow ${light ? "!bg-white/10 !text-green-300" : ""}`}>
        {eyebrow}
      </span>
    )}
    <h2
      className={`mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl ${
        light ? "text-white" : ""
      }`}
    >
      {title}
    </h2>
    {intro && (
      <p
        className={`mt-4 text-base leading-relaxed sm:text-lg ${
          light ? "text-white/70" : "text-brand-charcoal/80"
        }`}
      >
        {intro}
      </p>
    )}
  </Reveal>
);

export const WHATSAPP_URL =
  "https://wa.me/447774924524?text=Hello%20SafetySymbiosis%2C%20I%20would%20like%20to%20discuss%20an%20HSE%20requirement.";

export const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);
